import { format, subMonths } from 'date-fns';
import type { RentalManagerAuthCheckUserInfoWebApiDto } from '@zg-rentals/rental-manager-env';
import { ClientAdProductType, CometUserRoleType } from '@zg-rentals/rental-manager-env';
import { numberUtils } from '@zg-rentals/rental-manager-env';
import type {
  AdproductsZuoraBillingInvoiceDto,
  AdproductsZuoraBillingInvoiceItemDto,
  AdproductsZuoraInvoicePaymentDto,
  UserZuoraPaymentMethodDto,
} from '@zg-rentals/ts-adproducts-client';
import { localDateToDate } from './dateUtils';

const nonNucleusRoles = new Set<CometUserRoleType>([
  CometUserRoleType.MULTIFAMILY_CUSTOMER,
  CometUserRoleType.ROCKET_SHIP_CUSTOMER,
  CometUserRoleType.SINGLE_FAMILY_LEGACY,
  CometUserRoleType.ENTERPRISE_DOWNGRADED_EXPERIENCE,
  CometUserRoleType.SPOTLIGHT_CUSTOMER,
]);

const nonRocketshipRoles = new Set<CometUserRoleType>([
  CometUserRoleType.NUCLEUS_CUSTOMER,
  CometUserRoleType.MULTIFAMILY_CUSTOMER,
  CometUserRoleType.SINGLE_FAMILY_LEGACY,
]);

const billingUtils = {
  formatZuoraRefresh: (refreshTime: number) => {
    if (!refreshTime) return null;
    const date = new Date(refreshTime);
    return format(date, 'MM/dd/yyyy HH:mma');
  },
  isOnlyNucleusUser: (user: RentalManagerAuthCheckUserInfoWebApiDto) => {
    const roleTypes = user.roleTypes ?? [];
    return !roleTypes.some((role) => nonNucleusRoles.has(role));
  },
  isOnlyRocketshipUser: (user: RentalManagerAuthCheckUserInfoWebApiDto) => {
    const roleTypes = user.roleTypes ?? [];
    let rocketshipRoleFound = false;

    for (const i in roleTypes) {
      const role = roleTypes[i];
      if (nonRocketshipRoles.has(role)) {
        return false;
      }
      rocketshipRoleFound = rocketshipRoleFound || role === CometUserRoleType.ROCKET_SHIP_CUSTOMER;
    }
    return rocketshipRoleFound;
  },
  calculateServicePeriod: (invoiceItems: Array<AdproductsZuoraBillingInvoiceItemDto>) => {
    let servicePeriod = '';
    if (invoiceItems && invoiceItems.length !== 0) {
      const currMinStart = Math.min(...invoiceItems.map((element) => new Date(element.serviceStartDate).getTime()));
      const currMaxEnd = Math.max(...invoiceItems.map((element) => new Date(element.serviceEndDate).getTime()));
      servicePeriod = `${new Date(currMinStart).toLocaleDateString()} -
            ${new Date(currMaxEnd).toLocaleDateString()}`;
    }
    return servicePeriod;
  },
  validateInvoicePayment: (
    invoice: AdproductsZuoraBillingInvoiceDto,
    selectedCreditCardId: string,
    useBalance: boolean,
    customAmount: number,
  ) => {
    if (!selectedCreditCardId) {
      return 'No credit card selected.';
    }
    if (!useBalance && customAmount > invoice.balance) {
      return 'You cannot make a payment greater than the invoice balance.';
    }
    return null;
  },
  getNextAutoPaymentDate: () => {
    const now = new Date();
    const nextBillingDate = new Date();
    nextBillingDate.setDate(15);
    if (now.getDate() > 15) {
      nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);
    }
    return format(nextBillingDate, 'MM/dd/yyyy');
  },
  getCreditCardLastDigits: (maskNumber = '') => maskNumber.slice(maskNumber.length - 4, maskNumber.length),
  formatPaymentMethod: (paymentMethod: AdproductsZuoraInvoicePaymentDto | UserZuoraPaymentMethodDto) => {
    if (paymentMethod.paymentType === 'CreditCard') {
      return `${paymentMethod.brand} *${billingUtils.getCreditCardLastDigits(paymentMethod.maskNumber)}`;
    }
    if (paymentMethod.paymentType === 'ACH') {
      return `${paymentMethod.accountType} *${billingUtils.getCreditCardLastDigits(paymentMethod.maskNumber)}`;
    }
    return '-';
  },
  invoiceInAutopayRange: (invoice: AdproductsZuoraBillingInvoiceDto, date = new Date()) => {
    const chargeDay = new Date(date.getFullYear(), date.getMonth(), 15);
    const lastMonth = subMonths(date, 1);
    lastMonth.setDate(1);
    if (date > chargeDay) return false;
    if (localDateToDate(invoice.invoiceLocalDate) >= lastMonth) {
      return true;
    }
    return false;
  },
  formatDiscountRate(value: AdproductsZuoraBillingInvoiceItemDto) {
    const { discountPercent } = value;
    if (!discountPercent) return null;
    const total = value.chargeAmount / value.quantity;
    const discount = (100 - discountPercent) / 100;
    const originalValue = total / discount;
    const formattedOriginalValue = numberUtils.asCurrencyWithDefault(originalValue, '-');
    return `(${formattedOriginalValue} with ${discountPercent}% discount)`;
  },
  formatDiscountAmount(value: AdproductsZuoraBillingInvoiceItemDto) {
    const { discountPercent, chargeAmount } = value;
    if (!discountPercent) return null;
    const discount = (100 - discountPercent) / 100;
    const originalValue = chargeAmount / discount;
    const discountedAmount = originalValue - chargeAmount;
    const formattedDiscountAmount = numberUtils.asCurrencyWithDefault(discountedAmount, '-');
    return `(${formattedDiscountAmount} discounted)`;
  },
  isFlatFeeFeedConnectInvoice(invoiceLineItems: Array<AdproductsZuoraBillingInvoiceItemDto>) {
    if (!invoiceLineItems || !invoiceLineItems.length) {
      return false;
    }
    return invoiceLineItems
      .map((invoiceItem) => invoiceItem.productType)
      .every((productType) => productType === ClientAdProductType.FLAT_FEE_FEED_CONNECT);
  },
};

export default billingUtils;
